import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import GlobalFilters from "../components/GlobalFilters";
import MyTasks from "../components/MyTasks";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { PageHeader, num } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Layers, FilePlus2, FileDown, FileSpreadsheet } from "lucide-react";
import provincesGeo from "../data/drc_provinces.json";

const Card = ({ title, children, extra }) => (
  <div className="bg-white border border-slate-200/80 rounded-md shadow-sm p-5">
    <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold text-slate-800">{title}</h3>{extra}</div>{children}
  </div>
);
const mcolor = (r) => (r > 0.66 ? "#991B1B" : r > 0.33 ? "#C2410C" : "#0284C7");

export default function PillarDashboard() {
  const { code } = useParams();
  const [filters, setFilters] = useState({});
  const { t, lang } = useLang();
  const { data: cfg } = useConfig();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [mapMetric, setMapMetric] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["pillar-dash", code, filters, lang],
    queryFn: async () => (await api.get(`/pillar/${code}/dashboard`, { params: { ...filters, lang } })).data,
  });
  const { data: hist } = useQuery({
    queryKey: ["pillar-reports", code, filters],
    queryFn: async () => (await api.get(`/pillar/${code}/reports`, { params: { ...filters, limit: 50 } })).data,
  });
  const { data: mapData } = useQuery({
    queryKey: ["pillar-map", code, filters, mapMetric],
    queryFn: async () => (await api.get(`/pillar/${code}/map-data`, { params: { ...filters, metric: mapMetric || undefined } })).data,
  });

  const metList = (cfg?.pillar_ref?.[code]?.metrics) || [];
  const metMap = useMemo(() => Object.fromEntries(metList.map((m) => [m.code, m.name])), [metList]);
  const actMap = useMemo(() => Object.fromEntries(((cfg?.pillar_ref?.[code]?.activities) || []).map((a) => [a.code, a.name])), [cfg, code]);
  const zones = mapData?.zones || [];
  const maxV = Math.max(1, ...zones.map((z) => z.value || 0));

  const exportData = (fmt) => {
    const token = localStorage.getItem("token");
    const qs = new URLSearchParams({ ...filters, fmt }).toString();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pillar/${code}/export?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = `${code}_reports.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click(); URL.revokeObjectURL(url);
      });
  };

  return (
    <div data-testid="pillar-dashboard-page">
      <PageHeader title={`${data?.pillar_name || ""} · ${t("Tableau de Bord")}`} subtitle={t("Pilier · architecture partagée (géographie et indicateurs communs)")}>
        <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={() => navigate(`/pillar/${code}/new`)} data-testid="pillar-new-btn"><FilePlus2 className="h-4 w-4 mr-1" /> {t("Nouveau Rapport")}</Button>
      </PageHeader>
      <MyTasks />
      <GlobalFilters value={filters} onChange={setFilters} />

      {isLoading ? <p className="text-slate-400 text-sm">{t("Chargement…")}</p> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {(data.kpis || []).map((kp) => (
              <div key={kp.code} className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid={`pillar-kpi-${kp.code}`}>
                <p className="text-xs text-slate-500 mb-1">{kp.label}</p>
                <p className="text-2xl font-bold text-[#F05A28]">{num(kp.value)}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <Card title={`${t("Tendance")} · ${data.trend_metric}`}>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.trend}>
                  <defs><linearGradient id="pg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F05A28" stopOpacity={0.3} /><stop offset="95%" stopColor="#F05A28" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip />
                  <Area type="monotone" dataKey="value" name={data.trend_metric} stroke="#F05A28" fill="url(#pg)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card title={`${data.trend_metric} · ${t("par Zone de Santé")}`}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={(data.by_zone || []).slice(0, 10)} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="health_zone" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip /><Bar dataKey="value" name={data.trend_metric} fill="#0284C7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <div className="mt-5">
            <Card title={t("Carte du pilier")} extra={
              <div className="min-w-[200px]">
                <Select value={mapMetric || (mapData?.metric || "")} onValueChange={setMapMetric}>
                  <SelectTrigger className="h-8 text-xs" data-testid="pillar-map-metric"><SelectValue placeholder={t("Indicateur cartographié")} /></SelectTrigger>
                  <SelectContent>{(mapData?.metrics || metList).map((m) => <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>}>
              <div className="rounded-lg overflow-hidden" style={{ height: 420 }} data-testid="pillar-map">
                <MapContainer center={[-1.8, 29.2]} zoom={7} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                  <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <GeoJSON key={zones.length} data={provincesGeo} style={{ fillColor: "#e2e8f0", weight: 1, color: "#fff", fillOpacity: 0.3 }} />
                  {zones.map((z) => (
                    <CircleMarker key={z.health_zone} center={[z.lat, z.lng]} radius={7 + (z.value / maxV) * 22}
                      pathOptions={{ color: "#0f172a", weight: 1, fillColor: mcolor(z.value / maxV), fillOpacity: 0.85 }}>
                      <LTooltip><div className="text-xs"><p className="font-semibold">{z.health_zone} <span className="text-slate-400">({z.province})</span></p>
                        <p>{metMap[mapData?.metric] || ""}: <b>{num(z.value)}</b> · {t("Rapports")}: {z.reports}</p></div></LTooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
            </Card>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm mt-5 overflow-hidden" data-testid="pillar-history">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div><h3 className="text-sm font-semibold text-slate-800">{t("Historique des rapports")}</h3>
                <p className="text-xs text-slate-500">{t("Rapports reçus")}: <b>{hist?.total ?? data.reports_received}</b></p></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportData("csv")} data-testid="pillar-export-csv"><FileDown className="h-4 w-4 mr-1" /> {t("Exporter CSV")}</Button>
                <Button variant="outline" size="sm" onClick={() => exportData("xlsx")} data-testid="pillar-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" /> {t("Exporter Excel")}</Button>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[420px]">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>{t("Date")}</TableHead><TableHead>{t("Province")}</TableHead><TableHead>{t("Zone")}</TableHead>
                  <TableHead>{t("Activités")}</TableHead><TableHead>{t("Auteur")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(hist?.items || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">{t("Aucun rapport.")}</TableCell></TableRow>
                  ) : (hist.items.map((r) => (
                    <TableRow key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(r)} data-testid={`pillar-row-${r.id}`}>
                      <TableCell className="font-mono text-xs">{r.report_date}</TableCell>
                      <TableCell className="text-sm">{r.province}</TableCell>
                      <TableCell className="text-sm">{r.health_zone}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[280px]">{(r.activities || []).map((a) => actMap[a] || a).join(", ")}</TableCell>
                      <TableCell className="text-sm">{r.author_name}</TableCell>
                    </TableRow>
                  )))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (<>
            <DialogHeader><DialogTitle>{t("Détail du rapport")}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">{t("Localisation")}: <b>{detail.health_zone}</b> · {detail.province} · {detail.report_date}</p>
            <div className="flex flex-wrap gap-1.5 my-2">{(detail.activities || []).map((a) => <span key={a} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{actMap[a] || a}</span>)}</div>
            <div className="grid grid-cols-2 gap-2">
              {metList.map((m) => (
                <div key={m.code} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2">
                  <span className="text-xs text-slate-500">{m.name}</span>
                  <span className="text-sm font-semibold text-[#F05A28]">{num(detail.metrics?.[m.code] || 0)}</span>
                </div>
              ))}
            </div>
            {detail.note && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mt-2"><b>{t("Note")}:</b> {detail.note}</p>}
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
